import { parseBusinessProfilePayload, type BusinessTypeCode, type OrganizationBusinessProfile } from '../../shell/session/domain/businessProfile';

export type ChangeOrganizationBusinessTypeResult = {
  fromBusinessTypeCode: BusinessTypeCode;
  toBusinessTypeCode: BusinessTypeCode;
  businessProfile: OrganizationBusinessProfile;
  resetStats: {
    products_removed: number;
    products_deactivated: number;
    categories_removed: number;
    promotions_removed: number;
  };
  packApplied: boolean;
};

export type BusinessTypeChangeBlockers = {
  openCash: boolean;
  openOrders: boolean;
  activeAppointments: boolean;
};

export type BusinessTypeChangeDelta = {
  gain: string;
  lose: string;
};

export function parseChangeBusinessTypeResult(raw: unknown): ChangeOrganizationBusinessTypeResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const profile = parseBusinessProfilePayload(o.business_profile ?? null);
  if (!profile) return null;
  const resetStatsRaw = o.reset_stats;
  const resetStats =
    resetStatsRaw && typeof resetStatsRaw === 'object'
      ? (resetStatsRaw as Record<string, unknown>)
      : {};
  return {
    fromBusinessTypeCode: String(o.from_business_type_code ?? profile.business_type_code) as BusinessTypeCode,
    toBusinessTypeCode: String(o.to_business_type_code ?? profile.business_type_code) as BusinessTypeCode,
    businessProfile: profile,
    resetStats: {
      products_removed: Number(resetStats.products_removed ?? 0),
      products_deactivated: Number(resetStats.products_deactivated ?? 0),
      categories_removed: Number(resetStats.categories_removed ?? 0),
      promotions_removed: Number(resetStats.promotions_removed ?? 0),
    },
    packApplied: o.pack_result != null,
  };
}

export function parseBusinessTypeChangeBlockers(raw: unknown): BusinessTypeChangeBlockers | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (
    typeof o.open_cash !== 'boolean' ||
    typeof o.open_orders !== 'boolean' ||
    typeof o.active_appointments !== 'boolean'
  ) {
    return null;
  }
  return {
    openCash: o.open_cash,
    openOrders: o.open_orders,
    activeAppointments: o.active_appointments,
  };
}

export function businessTypeChangeBlockersClear(blockers: BusinessTypeChangeBlockers): boolean {
  return !blockers.openCash && !blockers.openOrders && !blockers.activeAppointments;
}

const GAIN_BY_TARGET: Record<string, string> = {
  neighborhood_store: 'Más estantes de comida y limpieza.',
  pharmacy_basic: 'Vencimientos y guardia en la tienda.',
  quick_service_food: 'Comandas de cocina.',
  food_retail: 'Comandas simples, sin mesas.',
  services_beauty: 'Agenda de turnos.',
  services_workshop: 'Catálogo de servicios y repuestos.',
  clothing_retail: 'Talles y colores.',
  beauty_retail: 'Variantes y vencimientos.',
  stationery_retail: 'Útiles y código de barras.',
  weight_retail: 'Venta por kilo.',
  hardware_retail: 'Por unidad o por metro.',
  general_retail: 'Seguís con mostrador. El nombre que escribiste se ve en Organización.',
  tienda_retail: 'Mostrador con catálogo y stock, sin atarte a un rubro específico.',
};

const LOSE_FROM_KIOSCO: Record<string, string> = {
  neighborhood_store: 'Se apagan recargas (SUBE y carga).',
  pharmacy_basic: 'Se apagan recargas.',
  quick_service_food: 'Se apagan cigarrillos y recargas.',
  food_retail: 'Se apagan recargas.',
  services_beauty: 'Se apagan recargas y el mostrador de kiosco.',
  services_workshop: 'Se apagan recargas.',
  general_retail: 'No nace un rubro nuevo hasta que haya varios iguales.',
};

export function businessTypeChangeDeltaCopy(fromCode: string, toCode: string): BusinessTypeChangeDelta {
  if (!toCode || fromCode === toCode) {
    return { gain: '', lose: '' };
  }
  const gain = GAIN_BY_TARGET[toCode] ?? 'Cambia cómo opera el local.';
  if (toCode === 'general_retail') {
    return { gain, lose: LOSE_FROM_KIOSCO.general_retail };
  }
  if (fromCode === 'retail_counter') {
    return { gain, lose: LOSE_FROM_KIOSCO[toCode] ?? 'Se apagan recargas.' };
  }
  return { gain, lose: '' };
}

export function mapChangeBusinessTypeError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('open cash session')) {
    return 'Cerrá la caja en todas las sucursales antes de cambiar el rubro.';
  }
  if (lower.includes('open customer orders')) {
    return 'Terminá o cancelá los pedidos online abiertos antes de cambiar el rubro.';
  }
  if (lower.includes('active appointments')) {
    return 'Cancelá o completá los turnos pendientes o confirmados antes de cambiar el rubro.';
  }
  if (lower.includes('owner required')) {
    return 'Solo el dueño de la cuenta puede cambiar el rubro.';
  }
  if (lower.includes('multi business profiles disabled')) {
    return 'El cambio de rubro no está habilitado.';
  }
  if (lower.includes('business type unchanged')) {
    return 'Elegí un rubro distinto al actual.';
  }
  if (lower.includes('invalid business type')) {
    return 'El rubro seleccionado no es válido.';
  }
  return message;
}
